import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { appRoutes, buildAiToolEditPath } from '../../app-routes';
import { PageHeader } from '../../components/common/PageHeader';
import { partnerApi, type ToolDefinition } from '../../lib/api';

export const ToolsListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['ai-tools'],
    queryFn: ({ pageParam }) => partnerApi.getTools({ cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage =>
      lastPage.pagination?.hasNextPage ? (lastPage.pagination.cursor ?? undefined) : undefined,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => partnerApi.archiveTool(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-tools'] }),
  });

  const tools = data?.pages.flatMap(page => page.tools) ?? [];

  return (
    <div className="container mx-auto max-w-5xl p-6">
      <PageHeader
        title="Agent Actions"
        description="Give Reach agents a few safe ways to use your API."
        right={
          <button
            type="button"
            onClick={() => navigate(appRoutes.AI_TOOL_NEW)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" />
            New action
          </button>
        }
      />

      {isLoading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : tools.length === 0 ? (
        <div className="rounded-xl border bg-white p-8 text-center text-sm text-muted-foreground">
          No actions yet. Click <strong>New action</strong> to create one.
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">What the agent can do</th>
                  <th className="px-4 py-3 font-medium">Access Level</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {tools.map(tool => (
                  <ToolRow
                    key={tool.id}
                    tool={tool}
                    onEdit={() => navigate(buildAiToolEditPath(tool.id))}
                    onArchive={() => {
                      if (
                        window.confirm(
                          `Archive ${tool.display_name}? It will stop being callable by agents.`,
                        )
                      ) {
                        archiveMutation.mutate(tool.id);
                      }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50">
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface ToolRowProps {
  tool: ToolDefinition;
  onEdit: () => void;
  onArchive: () => void;
}

const ToolRow = ({ tool, onEdit, onArchive }: ToolRowProps) => {
  const isReadOnly = Boolean(tool.annotations?.readOnlyHint);

  return (
    <tr className="border-t hover:bg-muted/50">
      <td className="px-4 py-3">
        <button type="button" onClick={onEdit} className="font-medium text-primary hover:underline">
          {humanizeName(tool.display_name)}
        </button>
        <div className="mt-1 font-mono text-xs text-muted-foreground">{tool.display_name}</div>
      </td>
      <td className="px-4 py-3 max-w-md truncate text-muted-foreground">{tool.description}</td>
      <td className="px-4 py-3">
        <span
          className={
            isReadOnly
              ? 'rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800'
              : 'rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800'
          }>
          {isReadOnly ? 'Read only' : 'Can change data'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={
            tool.status === 'active'
              ? 'rounded bg-green-100 px-2 py-0.5 text-xs text-green-800'
              : 'rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600'
          }>
          {tool.status}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <button type="button" onClick={onEdit} className="mr-2 text-sm text-primary hover:underline">
          Edit
        </button>
        <button
          type="button"
          onClick={onArchive}
          className="inline-flex items-center text-sm text-destructive hover:underline"
          aria-label={`Archive ${tool.display_name}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
};

const humanizeName = (name: string) =>
  name
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
